import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { productsAPI } from '@/utils/api';
import { Upload, Download, Trash2, FileImage, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DipBreadcrumb from '@/components/DipBreadcrumb';
import DipStepFooter from '@/components/DipStepFooter';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Labels = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadData(); }, [productId]);

  const loadData = async () => {
    try {
      const res = await productsAPI.getOne(productId);
      setProduct(res.data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/api/products/${productId}/label/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur upload');
      }
      toast.success('Étiquette ajoutée');
      const fileInput = document.getElementById('label-file-input');
      if (fileInput) fileInput.value = '';
      await loadData();
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'upload");
    } finally { setUploading(false); }
  };

  const handleDownload = async (labelId, filename) => {
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/label/${labelId}/download`);
      if (!res.ok) { toast.error('Étiquette indisponible'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'etiquette'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (labelId) => {
    if (!window.confirm('Supprimer cette étiquette ?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/products/${productId}/label/${labelId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('Étiquette supprimée');
      loadData();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) {
    return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;
  }

  const labels = product?.label_files || [];

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <DipBreadcrumb productId={productId} productName={product?.name} currentStep="labels" />

      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-foreground mb-2 tracking-tight">Étiquettes</h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          {product?.name} — Étiquettes du produit (obligatoire pour le DIP).
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Ajouter une étiquette</CardTitle>
          <CardDescription>Recto, verso, étiquette de lot, packaging — vous pouvez en ajouter plusieurs.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Input
              id="label-file-input"
              data-testid="input-label-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              disabled={uploading}
              onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); }}
            />
            <p className="text-xs text-muted-foreground">Formats acceptés : PDF, JPG, PNG, WEBP</p>
            <Button disabled className="w-full gap-2 hidden" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Étiquettes uploadées
            {labels.length > 0 ? (
              <span className="text-xs font-normal px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                {labels.length}
              </span>
            ) : (
              <span className="text-xs font-normal px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                <AlertCircle size={12} /> Manquant
              </span>
            )}
          </CardTitle>
          <CardDescription>Au moins une étiquette est nécessaire pour générer le DIP.</CardDescription>
        </CardHeader>
        <CardContent>
          {labels.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aucune étiquette ajoutée pour le moment.
            </p>
          ) : (
            <div className="space-y-3">
              {labels.map((label) => (
                <div key={label.id} className="flex items-center justify-between p-3 border border-border rounded-md hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <CheckCircle2 className="text-green-600 shrink-0" size={20} />
                    <FileImage className="text-primary shrink-0" size={20} strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{label.filename}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`btn-download-label-${label.id}`} size="sm" variant="outline"
                      onClick={() => handleDownload(label.id, label.filename)}>
                      <Download size={16} />
                    </Button>
                    <Button data-testid={`btn-delete-label-${label.id}`} size="sm" variant="ghost"
                      onClick={() => handleDelete(label.id)}
                      className="text-destructive hover:text-destructive">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {uploading && (
            <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2">
              <Upload size={14} className="animate-pulse" /> Upload en cours...
            </p>
          )}
        </CardContent>
      </Card>

      <DipStepFooter productId={productId} currentStep="labels" />
    </div>
  );
};

export default Labels;
